import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import API, { getMediaURL } from '../../../context/API';
import { Category, Customization, Item } from '../types';
import { QUERY_KEYS } from '../constants';

interface ItemsPage {
  items: Item[];
  total: number;
}

async function fetchItemsPage(page: number, limit: number, search: string): Promise<ItemsPage> {
  const response = await API.get(
    `/items/paginate?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`
  );
  const items: Item[] = response.data.data.map((item: Item) => ({
    ...item,
    picture: getMediaURL(item.picture),
  }));
  return { items, total: response.data.total };
}

export function useItemsQuery(search: string, page: number, pageSize: number) {
  return useQuery({
    queryKey: [QUERY_KEYS.ITEMS, search, page, pageSize],
    queryFn: () => fetchItemsPage(page, pageSize, search),
    staleTime: 2 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

export function useAllCustomizationsQuery(enabled = true) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [QUERY_KEYS.ALL_CUSTOMIZATIONS],
    queryFn: async (): Promise<Customization[]> => {
      const response = await API.get('/customizations');
      return response.data.success ? response.data.data : [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const refresh = () =>
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALL_CUSTOMIZATIONS] });
    window.addEventListener('customizationUpdated', refresh);
    window.addEventListener('customizationCreated', refresh);
    return () => {
      window.removeEventListener('customizationUpdated', refresh);
      window.removeEventListener('customizationCreated', refresh);
    };
  }, [queryClient]);

  return query;
}

export function useAllCategoriesQuery(enabled = true) {
  return useQuery({
    queryKey: [QUERY_KEYS.ALL_CATEGORIES],
    queryFn: async (): Promise<Category[]> => {
      const response = await API.get('/categories');
      return response.data.success ? response.data.data : [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useItemCustomizationsQuery(itemId: number | string | null) {
  return useQuery({
    queryKey: [QUERY_KEYS.ITEM_CUSTOMIZATIONS, itemId],
    queryFn: async (): Promise<Customization[]> => {
      const res = await API.get(`/items/${itemId}/customizations`);
      if (!res.data.success) return [];
      return res.data.data.customizations
        .map((c: any) => ({ ...c.customization, sequence_number: c.sequence_number }))
        .sort(
          (a: Customization, b: Customization) =>
            parseInt(a.sequence_number as string) - parseInt(b.sequence_number as string)
        );
    },
    enabled: !!itemId,
  });
}

export function useItemCategoriesQuery(itemId: number | string | null) {
  return useQuery({
    queryKey: [QUERY_KEYS.ITEM_CATEGORIES, itemId],
    queryFn: async (): Promise<Category[]> => {
      const res = await API.get(`/items/${itemId}/categories`);
      return res.data.success ? res.data.data.category_names : [];
    },
    enabled: !!itemId,
  });
}

export function useItemsStatsQuery() {
  return useQuery({
    queryKey: ['items-stats'],
    queryFn: async (): Promise<{ total: number; soldOutCount: number }> => {
      const res = await API.get('/items/paginate?page=1&limit=1');
      const total: number = res.data.total || 0;
      // Approximate sold out count from the paginated list if available
      const soldOutCount: number = res.data.soldOutCount ?? 0;
      return { total, soldOutCount };
    },
    staleTime: 2 * 60 * 1000,
  });
}
